import { useEffect, useState } from "react";
import { supabase } from "../supabaseClient";

export default function Unsubscribe() {
  const [status, setStatus] = useState("loading"); // loading | ready | submitting | success | already | invalid | error
  const [email, setEmail] = useState("");
  const [reason, setReason] = useState("");
  const [formError, setFormError] = useState("");

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const emailParam = params.get("email");

    if (!emailParam) {
      setStatus("invalid");
      return;
    }

    setEmail(emailParam);
    setStatus("ready");
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");

    if (!reason) {
      setFormError("Please select a reason before unsubscribing.");
      return;
    }

    if (!email) {
      setStatus("invalid");
      return;
    }

    setStatus("submitting");

    const { error } = await supabase
      .from("email_unsubscribes")
      .insert({ email, reason });

    if (!error) {
      setStatus("success");
    } else if (error.code === "23505") {
      // unique constraint: already unsubscribed
      setStatus("already");
    } else {
      console.error("Unsubscribe error:", error);
      setStatus("error");
    }
  };

  const baseStyle = {
    minHeight: "100vh",
    padding: "60px 16px",
    textAlign: "center",
    background: "#020617",
    color: "#e5e7eb",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  };

  const cardStyle = {
    maxWidth: 480,
    width: "100%",
    margin: "0 auto",
    background: "#020617",
    borderRadius: 16,
    border: "1px solid #1f2937",
    padding: "32px 24px",
    boxShadow: "0 18px 40px rgba(15,23,42,0.8)",
  };

  const headingStyle = {
    fontSize: 22,
    marginBottom: 8,
    fontWeight: 600,
  };

  const subTextStyle = {
    fontSize: 14,
    color: "#9ca3af",
    marginBottom: 20,
  };

  const labelStyle = {
    fontSize: 14,
    textAlign: "left",
    marginBottom: 8,
    display: "block",
  };

  const selectStyle = {
    width: "100%",
    padding: "10px 12px",
    borderRadius: 10,
    border: "1px solid #4b5563",
    background: "#020617",
    color: "#e5e7eb",
    fontSize: 14,
  };

  const buttonStyle = {
    marginTop: 16,
    width: "100%",
    padding: "10px 14px",
    borderRadius: 999,
    border: "none",
    background:
      "linear-gradient(90deg, #4f46e5, #ec4899, #f97316)",
    color: "#f9fafb",
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
    opacity: status === "submitting" ? 0.7 : 1,
  };

  const errorStyle = {
    marginTop: 8,
    fontSize: 13,
    color: "#f97373",
  };

  const smallEmailStyle = {
    marginTop: 6,
    fontSize: 12,
    color: "#9ca3af",
  };

  const retainerStyle = {
    marginTop: 16,
    fontSize: 13,
    color: "#9ca3af",
    lineHeight: 1.6,
  };

  return (
    <div style={baseStyle}>
      <div style={cardStyle}>
        {status === "loading" && (
          <>
            <h1 style={headingStyle}>Processing your request…</h1>
            <p style={subTextStyle}>
              Please wait while we get things ready.
            </p>
          </>
        )}

        {status === "ready" && (
          <>
            <h1 style={headingStyle}>Unsubscribe from Dreamframe Studio</h1>
            <p style={subTextStyle}>
              We’re sorry to see you go. Please tell us why you’re unsubscribing
              so we can improve.
            </p>
            {email && (
              <p style={smallEmailStyle}>
                Email: <span style={{ color: "#e5e7eb" }}>{email}</span>
              </p>
            )}

            <form onSubmit={handleSubmit} style={{ marginTop: 24 }}>
              <label style={labelStyle}>
                Reason for unsubscribing <span style={{ color: "#f97373" }}>*</span>
              </label>
              <select
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                style={selectStyle}
                required
              >
                <option value="">Select a reason</option>
                <option value="Too many emails">Too many emails</option>
                <option value="Content is not relevant to me">
                  Content is not relevant to me
                </option>
                <option value="I only signed up to try it once">
                  I only signed up to try it once
                </option>
                <option value="I’m using another platform">
                  I’m using another platform
                </option>
                <option value="I don’t remember signing up">
                  I don’t remember signing up
                </option>
                <option value="Other">Other</option>
              </select>

              {formError && <div style={errorStyle}>{formError}</div>}

              <button
                type="submit"
                style={buttonStyle}
                disabled={status === "submitting"}
              >
                {status === "submitting"
                  ? "Unsubscribing…"
                  : "Unsubscribe"}
              </button>
            </form>

            <p style={retainerStyle}>
              You’ll always be welcome back. If you decide to rejoin, you’ll
              find new AI-powered tools, creator challenges, and distribution
              opportunities waiting for you at Dreamframe Studio.
            </p>
          </>
        )}

        {status === "success" && (
          <>
            <h1 style={headingStyle}>You’ve been unsubscribed</h1>
            {email && (
              <p style={smallEmailStyle}>
                Email: <span style={{ color: "#e5e7eb" }}>{email}</span>
              </p>
            )}
            <p style={subTextStyle}>
              You will no longer receive marketing emails from Dreamframe Studio.
            </p>
            <p style={retainerStyle}>
              We’re grateful you tried Dreamframe Studio. If you ever decide to
              return, you’ll find fresh tools for AI storytelling, curated
              collaborators, and exciting new services ready for you.
            </p>
          </>
        )}

        {status === "already" && (
          <>
            <h1 style={headingStyle}>You’re already unsubscribed</h1>
            {email && (
              <p style={smallEmailStyle}>
                Email: <span style={{ color: "#e5e7eb" }}>{email}</span>
              </p>
            )}
            <p style={subTextStyle}>
              This address is already on our unsubscribe list, so you shouldn’t
              receive further marketing emails from Dreamframe Studio.
            </p>
            <p style={retainerStyle}>
              If you change your mind in the future, you can always sign back
              in and rejoin the community. We’ll be here with new features,
              smarter workflows, and more ways to bring your stories to life.
            </p>
          </>
        )}

        {status === "invalid" && (
          <>
            <h1 style={headingStyle}>Invalid unsubscribe link</h1>
            <p style={subTextStyle}>
              We couldn’t find a valid email in this link. Please use the
              unsubscribe link directly from one of our emails, or contact
              support if you need help.
            </p>
          </>
        )}

        {status === "error" && (
          <>
            <h1 style={headingStyle}>Something went wrong</h1>
            <p style={subTextStyle}>
              We couldn’t complete your unsubscribe request. Please try again in
              a few minutes, or contact support@dreamframe.studio if the
              problem persists.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
